/**
 * Logger Service
 *
 * Centralized logging service for the fga-manager web component.
 * Emits log events via an observable that can be captured by the host application.
 * Also optionally logs to the browser console.
 *
 * @packageDocumentation
 */

import { Injectable } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LogEvent, LogLevel, LOG_LEVEL_PRIORITY } from '../models';

/**
 * Centralized logging service for the fga-manager web component.
 *
 * Provides structured logging with multiple severity levels and emits
 * log events via an observable for integration with host applications.
 *
 * @remarks
 * - All log methods accept source, message, and optional data
 * - Events are emitted through `logs$` observable
 * - Console output can be enabled/disabled
 * - Minimum log level can be configured to filter events
 *
 * @example
 * ```typescript
 * // In a component or service
 * const logger = inject(LoggerService);
 *
 * // Log messages at different levels
 * logger.debug('FgaApiService', 'Request URL:', { url: '/read' });
 * logger.info('FgaManager', 'Component initialized');
 * logger.warn('TokenProvider', 'Token expiring soon');
 * logger.error('FgaApiService', 'Failed to fetch groups', error);
 *
 * // Subscribe to log events
 * logger.logs$.subscribe(event => {
 *   // Forward to external logging system
 *   sendToLogAggregator(event);
 * });
 * ```
 */
@Injectable({ providedIn: 'root' })
export class LoggerService {
  /** Subject for emitting log events */
  private readonly _logs$ = new Subject<LogEvent>();

  /** Minimum log level for emission (events below this level are filtered) */
  private _minLevel: LogLevel = 'debug';

  /** Whether to output logs to browser console */
  private _consoleOutput = false;

  /**
   * Observable stream of log events.
   * Subscribe to receive all log events at or above the minimum level.
   */
  readonly logs$: Observable<LogEvent> = this._logs$.asObservable().pipe(
    filter((event) => this.meetsMinLevel(event.level))
  );

  /**
   * Log a debug message.
   * Use for detailed diagnostic information during development.
   *
   * @param source - Component or service name (e.g., 'FgaApiService')
   * @param message - Log message
   * @param data - Optional contextual data
   */
  debug(source: string, message: string, data?: unknown): void {
    this.log('debug', source, message, data);
  }

  /**
   * Log an informational message.
   * Use for general operational information.
   *
   * @param source - Component or service name
   * @param message - Log message
   * @param data - Optional contextual data
   */
  info(source: string, message: string, data?: unknown): void {
    this.log('info', source, message, data);
  }

  /**
   * Log a warning message.
   * Use for potentially problematic situations.
   *
   * @param source - Component or service name
   * @param message - Log message
   * @param data - Optional contextual data
   */
  warn(source: string, message: string, data?: unknown): void {
    this.log('warn', source, message, data);
  }

  /**
   * Log an error message.
   * Use for errors and failures.
   *
   * @param source - Component or service name
   * @param message - Log message
   * @param data - Optional contextual data (e.g., error object)
   */
  error(source: string, message: string, data?: unknown): void {
    this.log('error', source, message, data);
  }

  /**
   * Log a message at a specified level.
   *
   * @param level - Log level
   * @param source - Component or service name
   * @param message - Log message
   * @param data - Optional contextual data
   */
  log(level: LogLevel, source: string, message: string, data?: unknown): void {
    const event: LogEvent = {
      level,
      message,
      source,
      timestamp: Date.now(),
      data,
    };

    // Output to console if enabled
    if (this._consoleOutput && this.meetsMinLevel(level)) {
      this.logToConsole(event);
    }

    // Emit event (filtering happens in the observable)
    this._logs$.next(event);
  }

  /**
   * Set whether to output logs to the browser console.
   *
   * @param enabled - True to enable console output, false to disable
   */
  setConsoleOutput(enabled: boolean): void {
    this._consoleOutput = enabled;
  }

  /**
   * Set the minimum log level for event emission.
   * Events below this level will be filtered out.
   *
   * @param level - Minimum log level ('debug', 'info', 'warn', 'error')
   */
  setMinLevel(level: LogLevel): void {
    this._minLevel = level;
  }

  /**
   * Get the current minimum log level.
   *
   * @returns Current minimum log level
   */
  getMinLevel(): LogLevel {
    return this._minLevel;
  }

  /**
   * Check if a log level meets the minimum level requirement.
   *
   * @param level - Log level to check
   * @returns True if level is at or above minimum
   */
  private meetsMinLevel(level: LogLevel): boolean {
    return LOG_LEVEL_PRIORITY[level] >= LOG_LEVEL_PRIORITY[this._minLevel];
  }

  /**
   * Output a log event to the browser console.
   *
   * @param event - Log event to output
   */
  private logToConsole(event: LogEvent): void {
    const prefix = `[${event.source}]`;

    switch (event.level) {
      case 'debug':
        console.debug(prefix, event.message, event.data);
        break;
      case 'info':
        console.info(prefix, event.message, event.data);
        break;
      case 'warn':
        console.warn(prefix, event.message, event.data);
        break;
      case 'error':
        console.error(prefix, event.message, event.data);
        break;
    }
  }
}
